/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  КОНСТРУКТОР ФОРМЫ УЧАСТНИКА — дополнительные поля текущего мероприятия
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ ЭТОТ СПРАВОЧНИК — НЕ ЭКРАН НАСТРОЕК, А СХЕМА ДАННЫХ ЭКРАНА УЧАСТНИКОВ.
 * Из него строятся:
 *   • колонки таблицы участников;
 *   • чекбоксы в форме создания и редактирования;
 *   • фильтры «отмечено / не отмечено»;
 *   • столбцы шаблона импорта (их собирает сервер, но в том же порядке).
 *
 * Поэтому он грузится вместе со скоупом мероприятия, а не по требованию: экран
 * участников без него отрисовать нельзя вообще, и ленивая загрузка означала бы
 * таблицу, у которой колонки появляются вторым кадром.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ПОРЯДОК СПИСКА — ЧАСТЬ КОНТРАКТА, ЕГО НЕЛЬЗЯ СОРТИРОВАТЬ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Сервер отдаёт поля в порядке `(position, createdAt)`, и в том же порядке
 * собирает столбцы шаблона импорта и выгрузки. Отсортировав список по алфавиту
 * «чтобы красивее», получим таблицу, чьи колонки не совпадают со столбцами
 * файла, — и оператора, который вписывает взвешивание в графу взноса.
 *
 * ⚠️ Сортировать по одному `position` тоже нельзя: значение не уникально и не
 * плотно (поля, заведённые до появления порядка, приехали с нулём), вторым
 * ключом на сервере идёт `createdAt`, а стабильности `Array.sort` по второму
 * ключу здесь никто не обещал. Список приходит отсортированным — его просто не
 * трогают.
 *
 * Менять порядок можно ровно одним способом — `reorder()`, и он присылает
 * ПОЛНЫЙ список id.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ПРАВА
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Читает любой участник мероприятия (без определений полей нельзя нарисовать
 * ни форму, ни список), пишет admin или владелец. Стор прав не проверяет —
 * это делает `stores/event.ts` (`canManage`), а последнее слово в любом случае
 * за сервером.
 */

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { participantFieldsApi } from '@/api/participant-fields'
import type { ApiError } from '@/lib/api-error'
import { normalizeError } from '@/lib/api-error'
import type { ParticipantField, ParticipantFieldType } from '@/types/api'
import { fieldsForRole } from '@/types/api'

/**
 * Что описывает поле в конструкторе — общая часть создания и правки.
 *
 * Объект, а не позиционные аргументы: у поля семь настроек, и `create(name,
 * true, '', [], false, '')` — это шесть шансов перепутать два соседних булевых
 * флага, причём молча.
 *
 * ⚠️ ВСЕ ПОЛЯ ОБЯЗАТЕЛЬНЫЕ, ХОТЯ У ПОЛОВИНЫ ЕСТЬ ОЧЕВИДНОЕ УМОЛЧАНИЕ. Запросы
 * на сервере НЕ частичные: пустая строка в `participantRoleId` снимает
 * привязку, пустая `description` убирает подсказку, `options` заменяет список
 * вариантов целиком. Необязательный ключ здесь означал бы, что забытое поле
 * тихо очищает настройку — то есть правка названия делает поле «взвешивание»
 * общим для всех.
 */
export interface ParticipantFieldInput {
  /**
   * Непустое, максимум `LIMITS.participantFieldNameMax`. Уникально **в
   * пределах области видимости**, а не мероприятия: у роли «Спортсмен» и у
   * роли «Тренер» могут быть свои «Взвешивания». Полное правило и три вида
   * отказа — в доке `ParticipantField.name`.
   */
  name: string
  /** Пустая строка — «применимо ко всем». Это выбор, а не «не заполнено». */
  participantRoleId: string
  highlightOnScan: boolean
  required: boolean
  description: string
  /** Только для `SELECT`; для остальных типов обязан быть пуст. */
  options: string[]
}

/** То же плюс тип — он задаётся ОДИН РАЗ, при создании, и потом не меняется. */
export interface CreateParticipantFieldInput extends ParticipantFieldInput {
  type: ParticipantFieldType
}

export const useParticipantFieldsStore = defineStore('participantFields', () => {
  /* ───────────────────────── state ───────────────────────── */

  /** Мероприятие, для которого загружен список. Смена id обнуляет данные. */
  const eventId = ref<string | null>(null)

  /** ⚠️ Порядок — серверный (`position`, затем `createdAt`). Не сортировать. */
  const items = ref<ParticipantField[]>([])

  const loading = ref(false)
  const error = shallowRef<ApiError | null>(null)

  /** Список загружен (успешно или нет) — до этого колонки рисовать нечем. */
  const ready = ref(false)

  /* ───────────────────────── getters ─────────────────────── */

  const isEmpty = computed(() => items.value.length === 0)

  /**
   * Поля, которые сканер обязан подсветить. Смысл флага в том, что на входе
   * важны две-три графы из десяти, и человеку на двери нужно увидеть именно их,
   * не читая всю карточку.
   */
  const highlighted = computed(() => items.value.filter((field) => field.highlightOnScan))

  /**
   * Поля, применимые к участнику с ролью `roleId` (`null` — роли нет).
   *
   * Это то, что видит форма участника, таблица и карточка сканирования.
   * Порядок сохраняется — см. шапку.
   */
  function forRole(roleId: string | null | undefined): ParticipantField[] {
    return fieldsForRole(items.value, roleId)
  }

  /**
   * Поля, не привязанные ни к какой роли, — то есть применимые ко всем.
   * Экран ролей показывает их отдельной группой над списком ролей.
   */
  const shared = computed(() => items.value.filter((field) => !field.participantRoleId))

  /** Поля, привязанные к конкретной роли. Ключ — id роли. */
  const byRole = computed(() => {
    const map = new Map<string, ParticipantField[]>()
    for (const field of items.value) {
      if (!field.participantRoleId) continue
      const list = map.get(field.participantRoleId)
      if (list) list.push(field)
      else map.set(field.participantRoleId, [field])
    }
    return map
  })

  /** Быстрый доступ по id — колонки таблицы и отчёт импорта ищут поле по нему. */
  const byId = computed(() => new Map(items.value.map((field) => [field.id, field])))

  /** Название поля по id; `''`, если поле удалили, пока экран был открыт. */
  function nameOf(fieldId: string): string {
    return byId.value.get(fieldId)?.name ?? ''
  }

  /* ───────────────────────── запрос ──────────────────────── */

  let controller: AbortController | null = null

  /**
   * Загрузить справочник мероприятия. Никогда не бросает.
   *
   * @param id     мероприятие
   * @param force  перечитать, даже если это то же мероприятие
   */
  async function load(id: string, force = false): Promise<void> {
    if (!force && eventId.value === id && ready.value && error.value === null) return

    controller?.abort()
    const ctl = new AbortController()
    controller = ctl

    // Смена мероприятия обязана сразу обнулить старые поля: иначе таблица
    // участников секунду рисует колонки чужого соревнования.
    if (eventId.value !== id) {
      items.value = []
      ready.value = false
    }

    eventId.value = id
    loading.value = true
    error.value = null

    try {
      const data = await participantFieldsApi.list(id, ctl.signal)
      if (controller !== ctl) return
      items.value = data.participantFields
    } catch (err) {
      if (controller !== ctl) return

      const apiError = normalizeError(err)
      if (apiError.isCanceled) return

      error.value = apiError
      items.value = []
    } finally {
      if (controller === ctl) {
        controller = null
        loading.value = false
        ready.value = true
      }
    }
  }

  /* ───────────────────────── мутации ─────────────────────── */

  /**
   * Все четыре **бросают** `ApiError` (вызывающий заворачивает в
   * `useAsyncAction`). Три из них заканчиваются перечитыванием списка, а не
   * локальной правкой массива: порядок здесь серверный и значащий (см. шапку),
   * а угадывать, куда встанет новая запись, — это второй источник правды.
   *
   * Исключение — `reorder()`: там перезапрос как раз не нужен, потому что
   * сервер возвращает список целиком и в новом порядке.
   */
  async function create(input: CreateParticipantFieldInput): Promise<ParticipantField> {
    const id = requireEventId()
    const response = await participantFieldsApi.create(id, {
      name: input.name,
      // Тип передаём явно, а не нулём: `UNSPECIFIED` сервер трактует как
      // CHECKBOX ради старых клиентов, но полагаться на это умолчание там, где
      // тип выбирает человек, — значит однажды молча завести галочку вместо
      // даты. Изменить это потом будет нечем: тип не обновляется.
      type: input.type,
      participantRoleId: input.participantRoleId,
      highlightOnScan: input.highlightOnScan,
      options: input.options,
      required: input.required,
      description: input.description,
    })
    await load(id, true)
    return response.participantField
  }

  /**
   * ⚠️ ПЕРЕДАЁТСЯ ЖЕЛАЕМОЕ СОСТОЯНИЕ ЦЕЛИКОМ, А НЕ ДЕЛЬТА. PUT не частичный:
   * пустая строка в `participantRoleId` снимает привязку, пустая
   * `description` убирает подсказку, `options` заменяет список вариантов
   * целиком. Поэтому вход — тот же объект, что у создания, минус тип.
   *
   * Тип НЕ меняется вовсе: у уже проставленных значений нет осмысленного
   * перевода из галочки в текст. Нужен другой тип — заводится другое поле.
   */
  async function update(
    fieldId: string,
    input: ParticipantFieldInput,
  ): Promise<ParticipantField> {
    const id = requireEventId()
    const response = await participantFieldsApi.update(id, fieldId, {
      name: input.name,
      participantRoleId: input.participantRoleId,
      highlightOnScan: input.highlightOnScan,
      options: input.options,
      required: input.required,
      description: input.description,
    })
    await load(id, true)
    return response.participantField
  }

  /**
   * Новый порядок формы. `fieldIds` — **ВСЕ** поля мероприятия, каждое ровно
   * один раз.
   *
   * ⚠️ ПОЛНОТА СПИСКА ПРОВЕРЯЕТСЯ ЗДЕСЬ, ДО ЗАПРОСА. Сервер отвергает неполный
   * список как `VALIDATION_FAILED`, и это правильно, но перетаскивание внутри
   * ОТФИЛЬТРОВАННОГО списка (например, только полей одной роли — так устроен
   * `ParticipantFieldsPanel`) даёт именно неполный список, и ошибка от сервера
   * пришла бы на экран как «что-то пошло не так» вместо ясного «собирайте
   * порядок из `items`». Программистская ошибка ловится `Error`, а не тостом.
   *
   * ⚠️ ПЕРЕСТАНОВКА ОПТИМИСТИЧНАЯ, С ОТКАТОМ. Причина не в скорости: элемент,
   * который человек только что перетащил, обязан остаться там, куда его
   * положили, — иначе следующее перетаскивание он начнёт делать по старому
   * списку, пока летит запрос. При отказе список возвращается ровно к тому,
   * что было (снимок, а не «переставить обратно» — обратная перестановка при
   * двух быстрых перетаскиваниях восстановила бы не то состояние).
   *
   * Успех кладёт в `items` ОТВЕТ сервера, а не локальную догадку: сервер
   * возвращает список целиком и в новом порядке, и доверять здесь надо ему.
   */
  async function reorder(fieldIds: string[]): Promise<ParticipantField[]> {
    const id = requireEventId()

    const known = new Map(items.value.map((field) => [field.id, field]))
    const unique = new Set(fieldIds)
    if (unique.size !== fieldIds.length || fieldIds.length !== known.size
      || fieldIds.some((fieldId) => !known.has(fieldId))) {
      throw new Error(
        '[participantFields] reorder требует ВСЕ поля мероприятия ровно по разу',
      )
    }

    const snapshot = items.value
    items.value = fieldIds.map((fieldId) => known.get(fieldId)!)

    try {
      const response = await participantFieldsApi.reorder(id, fieldIds)
      // Ответ мог разойтись со снимком не только порядком: пока тянули мышью,
      // сосед мог переименовать поле. Берём список сервера целиком.
      items.value = response.participantFields
      return response.participantFields
    } catch (err) {
      items.value = snapshot
      throw err
    }
  }

  /**
   * ⚠️ Удаление поля каскадом стирает **все проставленные по нему значения** у
   * всех участников мероприятия. Подтверждение обязано сказать это словами —
   * см. `ParticipantFieldsView`.
   */
  async function remove(fieldId: string): Promise<ParticipantField> {
    const id = requireEventId()
    const response = await participantFieldsApi.remove(id, fieldId)
    await load(id, true)
    return response.participantField
  }

  function requireEventId(): string {
    const id = eventId.value
    if (!id) throw new Error('[participantFields] мутация вне скоупа мероприятия')
    return id
  }

  /* ───────────────────────── $reset ──────────────────────── */

  function $reset(): void {
    controller?.abort()
    controller = null

    eventId.value = null
    items.value = []
    loading.value = false
    error.value = null
    ready.value = false
  }

  return {
    // state
    eventId,
    items,
    loading,
    error,
    ready,
    // getters
    isEmpty,
    highlighted,
    shared,
    byRole,
    forRole,
    byId,
    nameOf,
    // actions
    load,
    create,
    update,
    reorder,
    remove,
    $reset,
  }
})
